import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";

export default function Activate() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [verifyState, setVerifyState] = useState("loading"); // loading | ok | error
  const [verifyError, setVerifyError] = useState("");
  const [accountInfo, setAccountInfo] = useState(null); // {email, name, org?}

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  const strength = useMemo(() => {
    if (!pw) return { score: 0, label: "" };
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[a-z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return { score: s, label: ["Very weak", "Weak", "OK", "Good", "Strong", "Excellent"][s] };
  }, [pw]);

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!token) {
        setVerifyState("error");
        setVerifyError("Missing activation token. Please use the link from your welcome email.");
        return;
      }
      try {
        const res = await api.get(`/auth/activate/verify/${token}`);
        if (cancelled) return;
        setAccountInfo(res.data || {});
        setVerifyState("ok");
      } catch (err) {
        if (cancelled) return;
        setVerifyState("error");
        setVerifyError(formatApiError(err) || "This activation link is invalid or has expired.");
      }
    }
    verify();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (pw.length < 8) { setSubmitError("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setSubmitError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/activate", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setSubmitError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm p-8 login-fade-in">
        <div className="flex items-center gap-3 mb-6" data-testid="activate-brand">
          <img src="/brand/icon-192.png" alt="Ayobell" className="w-11 h-11 object-contain" />
          <div>
            <div className="font-heading font-bold tracking-[0.14em] text-slate-900 text-[20px] leading-none">AYOBELL</div>
            <div className="text-[10px] text-slate-500 tracking-[0.18em] uppercase mt-1">Care Management Platform</div>
          </div>
        </div>

        {verifyState === "loading" && (
          <div className="text-center py-8" data-testid="activate-verifying">
            <Loader2 className="w-6 h-6 text-slate-500 animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-600">Verifying your activation link…</p>
          </div>
        )}

        {verifyState === "error" && (
          <div className="text-center space-y-4" data-testid="activate-invalid">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-red-700" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">Activation link problem</h2>
            <p className="text-sm text-slate-600" data-testid="activate-error-message">{verifyError}</p>
            <div className="pt-2">
              <Link to="/login" data-testid="btn-back-login-invalid" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900 text-xs">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </div>
          </div>
        )}

        {verifyState === "ok" && !done && (
          <>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5 text-slate-700" />
              <h2 className="font-heading text-2xl font-semibold text-slate-900">Activate your account</h2>
            </div>
            <p className="text-sm text-slate-500 mb-2">
              Welcome{accountInfo?.name ? `, ${accountInfo.name}` : ""}. Set a password to finish activating your Ayobell Care account.
            </p>
            {accountInfo?.email && (
              <p className="text-xs text-slate-500 mb-6" data-testid="activate-email">
                Signing in as <span className="font-medium text-slate-700">{accountInfo.email}</span>
              </p>
            )}

            <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
              <div>
                <Label htmlFor="pw" className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
                  New password
                </Label>
                <Input
                  id="pw" data-testid="activate-pw"
                  type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  required autoComplete="new-password" minLength={8}
                  className="mt-2 h-11 login-input"
                />
                {pw && (
                  <div className="mt-2 flex items-center gap-2" data-testid="activate-strength">
                    <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-200 ${
                          strength.score <= 2 ? "bg-red-500" : strength.score <= 3 ? "bg-amber-500" : "bg-emerald-500"
                        }`}
                        style={{ width: `${(strength.score / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{strength.label}</span>
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="confirm" className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
                  Confirm password
                </Label>
                <Input
                  id="confirm" data-testid="activate-confirm"
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required autoComplete="new-password" minLength={8}
                  className="mt-2 h-11 login-input"
                />
              </div>
              {submitError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded" data-testid="activate-error">
                  {submitError}
                </div>
              )}
              <Button
                type="submit" disabled={submitting || !pw || !confirm} data-testid="activate-submit"
                className="w-full h-11 bg-[#0F172A] hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-[1px] hover:shadow-lg"
              >
                {submitting ? "Activating…" : "Activate account"}
              </Button>
            </form>

            <div className="mt-6 text-xs">
              <Link to="/login" data-testid="btn-back-login" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </div>
          </>
        )}

        {done && (
          <div className="text-center space-y-4" data-testid="activate-success">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-700" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">Account activated</h2>
            <p className="text-sm text-slate-600">Redirecting you to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
